import { useState } from 'react';
import { DAY_NAMES, HOURS, MINUTES } from '../../constants/openingHours';
import {
  Button,
  Typography,
  Checkbox,
  MenuItem,
  Stack,
  TextField,
  IconButton,
  Divider,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
const TimeSelect = ({ value, onChange }) => {
  const [hour = '00', minute = '00'] = String(value || '00:00').split(':');
  return (
    <Stack
      dir="ltr"
      sx={(theme) => ({
        display: 'inline-grid',
        gridTemplateColumns: '76px 10px 76px',
        alignItems: 'center',
        gap: '4px',
        '& select': {
          height: '34px',
          padding: '4px 8px',
          textAlign: 'left',
        },
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr 10px 1fr',
          width: '100%',
        },
      })}
    >
      <TextField
        value={hour.padStart(2, '0')}
        onChange={(e) => onChange(`${e.target.value}:${minute.padStart(2, '0')}`)}
        select
        fullWidth
      >
        {HOURS.map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </TextField>
      <Typography>:</Typography>
      <TextField
        value={minute.padStart(2, '0')}
        onChange={(e) => onChange(`${hour.padStart(2, '0')}:${e.target.value}`)}
        select
        fullWidth
      >
        {MINUTES.map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};
const OpeningHoursEditor = ({
  days,
  addRange,
  removeRange,
  updateRange,
  duplicateDay,
  duplicateTargets,
  setDuplicateTargets,
  setOpen247,
  isOpen247,
  onDaysChange,
}) => {
  const [localDuplicateTargets, setLocalDuplicateTargets] = useState(
    DAY_NAMES.map((_, day) => (day + 1) % 7),
  );
  const targets = duplicateTargets || localDuplicateTargets;
  const setTargets = setDuplicateTargets || setLocalDuplicateTargets;
  const changeDayRanges = (dayIndex, updater) => {
    onDaysChange((current) =>
      current.map((day) =>
        day.day === dayIndex
          ? {
            ...day,
            ranges: updater(day.ranges),
          }
          : day,
      ),
    );
  };
  const handleAddRange =
    addRange ||
    ((dayIndex) =>
      changeDayRanges(dayIndex, (ranges) => [
        ...ranges,
        {
          open: '08:00',
          close: '17:00',
          isOpen24Hours: false,
        },
      ]));
  const handleRemoveRange =
    removeRange ||
    ((dayIndex, rangeIndex) =>
      changeDayRanges(dayIndex, (ranges) => ranges.filter((_, index) => index !== rangeIndex)));
  const handleUpdateRange =
    updateRange ||
    ((dayIndex, rangeIndex, key, value) =>
      changeDayRanges(dayIndex, (ranges) =>
        ranges.map((range, index) =>
          index === rangeIndex
            ? {
              ...range,
              [key]: value,
            }
            : range,
        ),
      ));
  const handleDuplicateDay =
    duplicateDay ||
    ((sourceDay, targetDay) => {
      onDaysChange((current) => {
        const source = current.find((day) => day.day === sourceDay);
        return current.map((day) =>
          day.day === targetDay
            ? {
              ...day,
              ranges: source.ranges.map((range) => ({
                ...range,
              })),
            }
            : day,
        );
      });
    });
  const calculatedOpen247 =
    isOpen247 ?? days.every((day) => day.ranges.length === 1 && day.ranges[0].isOpen24Hours);
  const handleOpen247 =
    setOpen247 ||
    ((checked) =>
      onDaysChange((current) =>
        current.map((day) => ({
          ...day,
          ranges: checked
            ? [
              {
                open: '00:00',
                close: '24:00',
                isOpen24Hours: true,
              },
            ]
            : [],
        })),
      ));
  return (
    <Stack
      sx={{
        width: "100%",
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: '16px',
        background: '#fff',
      }}
    >
      <Stack
        sx={{
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <Stack
          direction="row"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            minHeight: '40px',
            color: 'var(--text)',
          }}
        >
          <Checkbox checked={calculatedOpen247} onChange={(e) => handleOpen247(e.target.checked)} />
          <Typography>פתוח 24/7</Typography>
        </Stack>
      </Stack>
      <Stack
        sx={{
          border: '1px solid var(--line)',
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: '120px minmax(0, 1fr) 260px',
            gap: '12px',
            alignItems: 'start',
            padding: '12px',
            color: 'var(--muted)',
            background: '#f8fafc',
            fontSize: '12px',
            fontWeight: '800',
            [theme.breakpoints.down('lg')]: {
              display: 'none',
            },
          })}
        >
          <Typography>יום</Typography>
          <Typography>טווחים</Typography>
          <Typography>פעולות</Typography>
        </Stack>
        {days.map((day) => (
          <Stack
            key={day.day}
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: '120px minmax(0, 1fr) 260px',
              gap: '12px',
              alignItems: 'start',
              padding: '12px',
              borderTop: '1px solid var(--line)',
              [theme.breakpoints.down('lg')]: {
                gridTemplateColumns: '1fr',
              },
            })}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
              }}
            >
              <IconButton onClick={() => handleAddRange(day.day)}>
                <Add color="primary" />
              </IconButton>
              <Typography>{day.name}</Typography>
            </Stack>

            <Stack spacing={2}>
              {day.ranges.length === 0 && (
                <Typography
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: '34px',
                    color: 'var(--muted)',
                  }}
                >
                  סגור
                </Typography>
              )}

              {day.ranges.map((range, rangeIndex) => (
                <Stack
                  key={`${day.day}-${rangeIndex}`}
                  direction="row"
                  sx={(theme) => ({
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    [theme.breakpoints.down('sm')]: {
                      alignItems: 'stretch',
                    },
                  })}
                >
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox
                      checked={range.isOpen24Hours}
                      onChange={(e) =>
                        handleUpdateRange(day.day, rangeIndex, 'isOpen24Hours', e.target.checked)
                      }
                    />
                    24 שעות
                  </Stack>

                  <Divider orientation="vertical" flexItem />

                  {!range.isOpen24Hours && (
                    <>
                      <TimeSelect
                        value={range.open}
                        onChange={(value) => handleUpdateRange(day.day, rangeIndex, 'open', value)}
                      />
                      <Typography>עד</Typography>
                      <TimeSelect
                        value={range.close}
                        onChange={(value) => handleUpdateRange(day.day, rangeIndex, 'close', value)}
                      />
                    </>
                  )}

                  <Divider orientation="vertical" flexItem />

                  <IconButton
                    type="button"
                    onClick={() => handleRemoveRange(day.day, rangeIndex)}
                    sx={{
                      borderRadius: 1,
                      minHeight: '30px',
                      height: '30px',
                      padding: '0 12px',
                      fontSize: '13px',
                      borderRightColor: '#d92d20',
                      background: '#fee4e2',
                      color: 'var(--danger)',
                      '&:disabled': {
                        opacity: '.45',
                        cursor: 'not-allowed',
                        transform: 'none',
                      },
                    }}
                  >
                    <Close
                      sx={{
                        fontSize: 'small',
                      }}
                    />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Stack
              direction="row"
              sx={(theme) => ({
                justifyContent: 'flex-end',
                gap: '8px',
                '& select': {
                  width: '92px',
                  height: '34px',
                  padding: '4px 8px',
                },
                [theme.breakpoints.down('lg')]: {
                  justifyContent: 'flex-start',
                },
              })}
            >
              <TextField
                value={targets[day.day]}
                onChange={(e) =>
                  setTargets((current) =>
                    current.map((value, index) =>
                      index === day.day ? Number(e.target.value) : value,
                    ),
                  )
                }
                select
                fullWidth
              >
                {DAY_NAMES.map((name, index) => (
                  <MenuItem key={name} value={index} disabled={index === day.day}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <Button onClick={() => handleDuplicateDay(day.day, targets[day.day])}>שכפל</Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
export default OpeningHoursEditor;
